/**
 * The browser history as the app sees it. The address bar holds the short address (`/project/PJ-90F0D6B4/.../PO-130`);
 * everything inside the app (useLocation, Link, navigate, every page's id parsing) keeps seeing the app form
 * (`/.../PO-<uuid>`). This object translates at that edge:
 *
 *   location / listen   decode: strip the project, turn PO-130 / PO-90F0D6B4 into PO-<uuid>
 *   push / replace / createHref   encode: PO-<uuid> into PO-130 for the kinds switched on, add the project
 *
 * No page needs to know. Old addresses are already in the app form, so they pass through unchanged.
 */
import type { Location, To } from 'react-router-dom';
import type { UNSAFE_createBrowserHistory as createBrowserHistory } from 'react-router-dom';

// react-router 7 no longer exports its History types; derive them from the factory the router itself accepts.
type History = ReturnType<typeof createBrowserHistory>;
type Listener = Parameters<History['listen']>[0];
import { getActiveProjectId } from '../utils/session';
import {
  decodeRecordSegments, encodeRecordSegments, splitHref, splitProjectPath, withProjectPath,
  type NumberLookup, type UnresolvedRef,
} from '../utils/routeRefs';
import { findRecordIdByHash, findRecordIdByNumber, getRecordRef } from '../utils/recordRefs';
import { getResolution, resolvedIds } from './routeResolution';
import { routeRefConfig } from './routeRefConfig';
import type { ResolvedCandidate } from '../services/recordRefResolver';

const storeLookup: NumberLookup = {
  idByNumber: (kind, n) => findRecordIdByNumber(kind, n),
  idByHash: (kind, hex) => findRecordIdByHash(kind, hex),
  numberOf: (kind, uuid) => getRecordRef(kind, uuid),
};
const NO_LOOKUP: Pick<NumberLookup, 'idByNumber' | 'idByHash'> = { idByNumber: () => undefined, idByHash: () => undefined };

export interface Decoded {
  /** The location the app sees. */
  location: Location;
  /** Short references still to ask the API about (not answered, not known from the loaded numbers). */
  pending: UnresolvedRef[];
  /** A hash8 that two records share: the person picks. */
  ambiguous: { segment: string; candidates: ResolvedCandidate[] } | null;
  /** `/project/PJ-x` in the address, as its 8 hex characters. */
  urlProjectHex: string | null;
  /** The address names another project than the active one: numbers cannot be read until that project is opened. */
  projectMismatch: boolean;
}

export function decodeLocation(loc: Location, activeProjectId: string | null = getActiveProjectId()): Decoded {
  const { projectHex, rest } = splitProjectPath(loc.pathname);
  const mismatch = projectHex !== null && !(activeProjectId ?? '').toLowerCase().startsWith(projectHex);
  const scopeId = mismatch ? null : activeProjectId;
  const { path, unresolved } = decodeRecordSegments(rest, scopeId ? storeLookup : NO_LOOKUP, resolvedIds(scopeId));

  let ambiguous: Decoded['ambiguous'] = null;
  const pending = unresolved.filter(u => {
    const e = getResolution(scopeId, u.segment);
    if (!e) return true;
    if (e.status === 'ambiguous') ambiguous = { segment: u.segment, candidates: e.candidates };
    return false;
  });
  return { location: { ...loc, pathname: path }, pending, ambiguous, urlProjectHex: projectHex, projectMismatch: mismatch };
}

/** An app-form destination as the address bar should show it. */
export function encodeTo(to: To, activeProjectId: string | null = getActiveProjectId()): To {
  const encode = (pathname: string): string => {
    if (!pathname.startsWith('/')) return pathname;
    const short = routeRefConfig.encodeKinds.size > 0
      ? encodeRecordSegments(pathname, storeLookup.numberOf, routeRefConfig.encodeKinds)
      : pathname;
    return routeRefConfig.projectInPath ? withProjectPath(short, activeProjectId) : short;
  };
  if (typeof to === 'string') {
    const { pathname, search, hash } = splitHref(to);
    return `${encode(pathname)}${search}${hash}`;
  }
  return to.pathname === undefined ? to : { ...to, pathname: encode(to.pathname) };
}

export interface RefHistory extends History {
  /** Re-reads the address with the numbers known now, telling the router when the app form changed. */
  refresh(): void;
}

export function createRefHistory(base: History): RefHistory {
  let seen = '';
  // The browser history takes one listener; the router and RefRouter both listen, so fan out from one.
  const subs = new Set<Listener>();
  let stop: (() => void) | null = null;
  const view = (): Location => {
    const loc = decodeLocation(base.location).location;
    seen = loc.pathname;
    return loc;
  };
  return {
    get action() { return base.action; },
    get location() { return view(); },
    createHref: to => base.createHref(encodeTo(to)),
    createURL: to => base.createURL(encodeTo(to)),
    encodeLocation: to => base.encodeLocation(encodeTo(to)),
    push: (to, state) => base.push(encodeTo(to), state),
    replace: (to, state) => base.replace(encodeTo(to), state),
    go: delta => base.go(delta),
    listen(listener: Listener) {
      subs.add(listener);
      if (!stop) stop = base.listen(update => { const location = view(); subs.forEach(l => l({ ...update, location })); });
      return () => {
        subs.delete(listener);
        if (subs.size === 0 && stop) { stop(); stop = null; }
      };
    },
    refresh() {
      const now = decodeLocation(base.location).location.pathname;
      if (now === seen) return;
      const { pathname, search, hash, state } = base.location;
      base.replace({ pathname, search, hash }, state);
    },
  };
}
